<template>
    <MainContent
        title="Listado de precios"
        v-model="filters"
        :meta="meta"
        @updated="debouncedFetch"
    >
        <!-- Header: Card con buscador + botones (skeleton o real) -->
        <template #header>
            <template v-if="!hasLoadedOnce && loading">
                <Card customClasses="tw:mb-4 tw:px-4 tw:py-4">
                    <Skeleton padding="tw:p-0" variant="tw:bg-transparent" customClass="tw:w-full">
                        <template #skeleton>
                            <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-2 tw:md:gap-14 tw:justify-between">
                                <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:rounded-lg" />
                                <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-2 tw:w-full tw:justify-end">
                                    <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:md:w-[133px] tw:rounded-lg" />
                                    <SkeletonItem height="tw:h-10" customClass="tw:w-full tw:md:w-[204px] tw:rounded-lg" />
                                </div>
                            </div>
                        </template>
                    </Skeleton>
                </Card>
            </template>
            <template v-else>
                <Card customClasses="tw:mb-4 tw:px-4 tw:py-4">
                    <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-2 tw:md:gap-14 tw:justify-between tw:items-center">
                        <div class="tw:w-full">
                            <SearchComponent
                                :value="filters.search"
                                @input="val => { filters.search = val; filters.page = 1 }"
                            />
                        </div>
                        <div class="tw:flex tw:flex-col tw:md:flex-row tw:gap-2 tw:w-full tw:sm:justify-end">
                            <Button
                                class="tw:min-w-fit tw:justify-center"
                                @click="onShowCreateModal"
                            >
                                Nueva categoría
                            </Button>
                            <Button
                                class="tw:min-w-fit tw:justify-center"
                                as="router-link"
                                :to="{ name: 'back.frecuencias' }"
                            >
                                Frecuencias pago a plazos
                            </Button>
                        </div>
                    </div>
                </Card>
            </template>
        </template>

        <CategoriaList
            :categorias="categorias.data"
            :loading="loading"
            :lista-id="listaId"
            :invitacion-enabled="activarInvitacion"
            :edit-item="editItem"
            :delete-item="deleteItem"
            :edit-saving="editSaving"
            @open-edit="handleOpenEdit"
            @open-delete="handleOpenDelete"
            @edit-save="handleEditSave"
            @delete="handleDelete"
            @close-edit="handleCloseEdit"
            @close-delete="closeDelete"
        />

        <!-- Skeleton footer (solo primera carga) -->
        <template v-if="!hasLoadedOnce && loading">
            <Card :custom-classes="'tw:flex tw:flex-col tw:items-center tw:justify-between tw:gap-2 tw:pl-4 tw:pr-4 tw:py-2 tw:mt-6'">
                <Skeleton padding="tw:p-0" variant="tw:bg-transparent" customClass="tw:w-full">
                    <template #skeleton>
                        <div class="tw:flex tw:flex-col tw:w-full tw:md:flex-row tw:md:items-center tw:md:justify-between">
                            <div class="tw:flex tw:justify-between tw:items-center tw:gap-3">
                                <SkeletonItem height="tw:h-4" customClass="tw:w-20 tw:rounded tw:md:tw:hidden" />
                                <SkeletonItem height="tw:h-9" customClass="tw:w-18 tw:rounded-lg" />
                            </div>
                            <div class="tw:hidden tw:md:block">
                                <SkeletonItem height="tw:h-4" customClass="tw:w-48 tw:rounded tw:mx-auto" />
                            </div>
                            <div class="tw:flex tw:justify-center tw:items-center tw:gap-1">
                                <SkeletonItem v-for="n in 3" :key="'pag-sk-' + n" height="tw:h-8" customClass="tw:w-8 tw:rounded" />
                            </div>
                        </div>
                    </template>
                </Skeleton>
            </Card>
        </template>

        <CategoriaCreateModal
            name="create-categoria"
            :loading="createLoading"
            @save="handleCreate"
            @close="onCloseCreateModal"
        />
    </MainContent>
</template>

<script setup>
    import { ref, watch, nextTick, getCurrentInstance } from 'vue'
    import { useCategoriasIndex } from '@/composables/admin/bases/useCategoriasIndex'
    import MainContent from '@/modules/admin/ui/MainContent.vue'
    import Button from '@/modules/admin/ui/Button.vue'
    import Card from '@/modules/admin/ui/Card.vue'
    import SearchComponent from '@/modules/admin/ui/SearchComponent.vue'
    import CategoriaList from '../components/CategoriaList.vue'
    import CategoriaCreateModal from '../components/CategoriaCreateModal.vue'
    import { Skeleton, SkeletonItem } from '@/modules/admin/ui/skeleton'

    const { proxy } = getCurrentInstance()

    const editSaving = ref(false)

    const {
        categorias, loading, filters, meta, listaId, activarInvitacion, debouncedFetch, borrar,
        createLoading, crear, editar,
        editItem, openEdit, closeEdit,
        deleteItem, openDelete, closeDelete
    } = useCategoriasIndex()

    const hasLoadedOnce = ref(false)
    watch(categorias, val => {
        if (val?.data?.length) hasLoadedOnce.value = true
    })

    function onShowCreateModal() {
        proxy?.$modal?.show('create-categoria')
    }

    function onCloseCreateModal() {
        proxy?.$modal?.hide('create-categoria')
    }

    async function handleCreate(formData) {
        const ok = await crear(formData)
        if (ok) proxy?.$modal?.hide('create-categoria')
    }

    function handleOpenEdit(item) {
        openEdit(item)
        nextTick(() => proxy?.$modal?.show('edit-categoria'))
    }

    async function handleEditSave(formData) {
        editSaving.value = true
        await editar(formData)
        editSaving.value = false
    }

    function handleCloseEdit() {
        proxy?.$modal?.hide('edit-categoria')
        editSaving.value = false
        closeEdit()
    }

    function handleOpenDelete(item) {
        openDelete(item)
        nextTick(() => proxy?.$modal?.show('borrar-categoria'))
    }

    function handleDelete(id) {
        proxy?.$modal?.hide('borrar-categoria')
        closeDelete()
        borrar(id)
    }
</script>
